import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import axios from '../../utils/axiosConfig';
import './UserManagement.css';

const { TabPane } = Tabs;
const { Option } = Select;

function UserManagement() {
  const [activeTab, setActiveTab] = useState('admins');
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  const authHeader = useCallback(() => {
    const token = sessionStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  }, []);

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const hdr = authHeader();
      const { data } = await axios.get('/api/users?role=superadmin', hdr ? { headers: hdr } : undefined);
      let items = [];
      if (Array.isArray(data)) items = data;
      else if (data && Array.isArray(data.items)) items = data.items;
      setAdmins(items);
    } catch (error) {
      message.error('Failed to fetch admins: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      // TODO: Implement roles API endpoint
      // For now, use mock data
      setRoles([
        { id: 1, name: 'Super Admin', permissions: ['all'], description: 'Full system access' },
        { id: 2, name: 'Admin', permissions: ['read', 'write'], description: 'Standard admin access' },
        { id: 3, name: 'Agency', permissions: ['read', 'write_own'], description: 'Agency management' },
        { id: 4, name: 'Client', permissions: ['read_own'], description: 'Client access' },
      ]);
    } catch (error) {
      message.error('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'admins') {
      fetchAdmins();
    } else if (activeTab === 'roles') {
      fetchRoles();
    }
  }, [activeTab, fetchAdmins, fetchRoles]);

  const openModal = (type, record = null) => {
    setModalType(type);
    setEditingRecord(record);
    setModalVisible(true);
    if (record) {
      form.setFieldsValue(record);
    } else {
      form.resetFields();
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      if (modalType === 'admin') {
        const hdr = authHeader();
        if (editingRecord) {
          await axios.put(`/api/users/${editingRecord.id}`, values, hdr ? { headers: hdr } : undefined);
          message.success('Admin updated successfully');
        } else {
          await axios.post('/api/users', { ...values, role: 'superadmin' }, hdr ? { headers: hdr } : undefined);
          message.success('Admin created successfully');
        }
        fetchAdmins();
      } else if (modalType === 'role') {
        // TODO: Implement roles API
        message.success('Role saved successfully');
        fetchRoles();
      }
      closeModal();
    } catch (error) {
      message.error('Operation failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (type, id) => {
    try {
      if (type === 'admin') {
        const hdr = authHeader();
        await axios.delete(`/api/users/${id}`, hdr ? { headers: hdr } : undefined);
        message.success('Admin deleted successfully');
        fetchAdmins();
      } else if (type === 'role') {
        // TODO: Implement roles API
        message.success('Role deleted successfully');
        fetchRoles();
      }
    } catch (error) {
      message.error('Delete failed: ' + (error.response?.data?.error || error.message));
    }
  };

  const adminColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={role === 'superadmin' ? 'red' : 'orange'}>
          {role === 'superadmin' ? 'SUPER ADMIN' : 'ADMIN'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openModal('admin', record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this admin?"
            onConfirm={() => handleDelete('admin', record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const roleColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Role Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Permissions',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (permissions) => (
        <>
          {permissions?.map((perm, idx) => (
            <Tag key={idx} color="blue">{perm}</Tag>
          ))}
        </>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => openModal('role', record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this role?"
            onConfirm={() => handleDelete('role', record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const renderModalForm = () => {
    if (modalType === 'admin') {
      return (
        <>
          <Form.Item name="full_name" label="Full Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          {!editingRecord && (
            <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}>
              <Input.Password />
            </Form.Item>
          )}
        </>
      );
    } else if (modalType === 'role') {
      return (
        <>
          <Form.Item name="name" label="Role Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="permissions" label="Permissions" rules={[{ required: true }]}>
            <Select mode="multiple" placeholder="Select permissions">
              <Option value="all">All Permissions</Option>
              <Option value="read">Read</Option>
              <Option value="write">Write</Option>
              <Option value="delete">Delete</Option>
              <Option value="read_own">Read Own</Option>
              <Option value="write_own">Write Own</Option>
            </Select>
          </Form.Item>
        </>
      );
    }
  };

  return (
    <div className="user-management-container">
      <div className="page-header">
        <h1>User Management</h1>
        <p>Manage super admins, staff, and role permissions</p>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} className="user-management-tabs">
        <TabPane
          tab={
            <span>
              <UserOutlined />
              Super Admins & Staff
            </span>
          }
          key="admins"
        >
          <div className="tab-content">
            <div className="tab-header">
              <h2>Super Admins & Staff</h2>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openModal('admin')}
              >
                Add Admin
              </Button>
            </div>
            <Table
              columns={adminColumns}
              dataSource={admins}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </TabPane>

        <TabPane
          tab={
            <span>
              <SafetyCertificateOutlined />
              Role & Permission Settings
            </span>
          }
          key="roles"
        >
          <div className="tab-content">
            <div className="tab-header">
              <h2>Role & Permission Settings</h2>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => openModal('role')}
              >
                Add Role
              </Button>
            </div>
            <Table
              columns={roleColumns}
              dataSource={roles}
              rowKey="id"
              loading={loading}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </TabPane>
      </Tabs>

      <Modal
        title={`${editingRecord ? 'Edit' : 'Add'} ${modalType === 'admin' ? 'Admin' : 'Role'}`}
        open={modalVisible}
        onCancel={closeModal}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {renderModalForm()}
        </Form>
      </Modal>
    </div>
  );
}

export default UserManagement;
